import { onBeforeUnmount, watch } from "vue";
import store from "@/store/store";
import {
    actionTypes as websiteDatesActionTypes,
    mutationTypes as websiteDatesMutationTypes,
} from "@/store/WebsiteDates/types";
import {
    actionTypes as websiteActionTypes,
    mutationTypes as websiteMutationTypes,
    getterTypes as websiteGetterTypes,
} from "@/store/Websites/types";
import {
    actionTypes as publisherActionTypes,
    getterTypes as publisherGetterTypes,
    LS_KEYS,
} from "@/store/Publisher/types";
import { getItem } from "@/store/persistantStorage";
import { getDefaultPeriod } from "@/helpers/data";

/**
 * Composable for managing dashboard state and cleanup
 * Handles proper cleanup when switching between LTBA and Yield dashboards
 */
export function useDashboardState(dashboardType, route) {
    const isLtba = dashboardType === "ltba";
    const routeName = isLtba ? "dashboard-ltba" : "dashboard-yield";
    const otherRouteName = isLtba ? "dashboard-yield" : "dashboard-ltba";
    const namespace = isLtba ? LS_KEYS.DATES_LTBA : LS_KEYS.DATES_YIELD;

    /**
     * Clear transient display data (WebsiteDates, Websites stores)
     * Does NOT clear dashboard-specific selections (publisher, websites, period)
     */
    const clearDisplayData = () => {
        // Clear the shared WebsiteDates store
        store.commit(websiteDatesMutationTypes.setData, {
            aabSiteIds: [],
            period: null,
        });

        // Clear the main Websites store to prevent cross-contamination
        store.commit(websiteMutationTypes.websitesGetDataStart);
    };

    /**
     * Clear transient view stores. Does NOT touch selectedWebsitesLtba/Yield —
     * that would wipe the user's saved subset via the action's setItem side
     * effect (and break tab-toggle preservation). Callers that genuinely need
     * to drop the selection (e.g. publisher change) should dispatch the
     * setSelected*Websites action themselves.
     */
    const clearDashboardData = () => {
        clearDisplayData();
    };

    /**
     * Clear localStorage for the current dashboard
     */
    const clearLocalStorage = (userId, period) => {
        if (!userId) return;

        // Preserve user's previously chosen period (LS or publisher-level Vuex)
        // when caller didn't provide one — keeps period across mode switches/remounts.
        const resolvedPeriod = period || getStoredPeriod(userId) || getDefaultPeriod();

        store.dispatch(websiteDatesActionTypes.setWebsiteDatesToLS, {
            aabSiteIds: [],
            period: resolvedPeriod,
            userId: userId,
            namespace: namespace,
        });
    };

    /**
     * Load websites for the current publisher
     */
    const loadWebsites = async () => {
        return store.dispatch(websiteActionTypes.getData);
    };

    /**
     * Setup watchers for route changes
     */
    const setupRouteWatcher = (callback) => {
        return watch(
            () => route.name,
            async (newName, oldName) => {
                // Only clear transient Vuex view state when switching between LTBA and Yield.
                // Namespace LS keys (ltba_website_dates / yield_website_dates) are intentionally
                // preserved so the period survives the swap; stale aabSiteIds get refreshed
                // by getWebsiteDatesfromLS on the destination mount.
                if (oldName === routeName && newName === otherRouteName) {
                    clearDashboardData();
                }

                // Set selected mode when entering this dashboard
                if (newName === routeName) {
                    store.dispatch(publisherActionTypes.setSelectedMode, dashboardType);

                    if (callback) {
                        await callback(oldName, newName);
                    }
                }
            },
            { immediate: true }
        );
    };

    /**
     * Setup cleanup on component unmount
     * Only clears transient display data, preserves dashboard selections
     */
    const setupUnmountCleanup = () => {
        onBeforeUnmount(() => {
            // Only clear WebsiteDates store, keep everything else
            store.commit(websiteDatesMutationTypes.setData, {
                aabSiteIds: [],
                period: null,
            });
        });
    };

    /**
     * Clear and reload data when switching publishers
     */
    const handlePublisherChange = async (publisherId, userId, period) => {
        // Clear display data and localStorage
        clearDisplayData();
        clearLocalStorage(userId, period);

        // Load new websites if publisher is selected
        if (publisherId) {
            await loadWebsites();
            return true;
        }
        return false;
    };

    /**
     * Handle single publisher selection (for Yield dashboard)
     */
    const handleSinglePublisherSelection = async (publisher) => {
        clearDisplayData();

        if (publisher) {
            await loadWebsites();
            return true;
        }
        return false;
    };

    /**
     * Load websites and set them in the appropriate store
     * @param {number} publisherId - Publisher ID to filter by (only for LTBA)
     * @param {number} userId - User ID for localStorage
     * @param {string} period - Period for localStorage
     */
    const loadAndSetWebsitesForPublisher = async (publisherId, userId, period) => {
        await store.dispatch(websiteActionTypes.getData);
        const loadedWebsites = store.getters[websiteGetterTypes.getWebsites];

        if (loadedWebsites && loadedWebsites.length > 0) {
            // Websites available for this dashboard scope (LTBA filters by publisher).
            let availableWebsites = loadedWebsites;
            if (isLtba && publisherId) {
                availableWebsites = loadedWebsites.filter((web) => web.publisher_id === publisherId);
            }

            // Preserve the user's previously chosen subset (restored from LS on remount).
            // Intersecting with availableWebsites drops stale ids that no longer exist or
            // don't belong to the current publisher (LTBA). Falls back to "all available"
            // when nothing valid was previously selected.
            let currentlySelected = isLtba
                ? store.getters[publisherGetterTypes.getSelectedWebsitesLtba]
                : store.getters[publisherGetterTypes.getSelectedWebsitesYield];

            // Vuex may not yet hold the LS-restored value during a tab-toggle remount
            // (race between restoreFromLS dispatch and this watcher firing). Fall back
            // to LS directly so the user's saved subset survives Yield ↔ LTBA toggling.
            if (!currentlySelected || currentlySelected.length === 0) {
                const fromLS = getItem(isLtba ? LS_KEYS.WEBSITES_LTBA : LS_KEYS.WEBSITES_YIELD);
                if (Array.isArray(fromLS) && fromLS.length > 0) {
                    currentlySelected = fromLS;
                }
            }

            const availableIds = new Set(availableWebsites.map((w) => w.aab_site_id));
            const preserved = (currentlySelected || []).filter((w) => availableIds.has(w.aab_site_id));
            const websitesToSet = preserved.length > 0 ? preserved : availableWebsites;

            // Set websites in appropriate store
            if (isLtba) {
                store.dispatch(publisherActionTypes.setSelectedWebsitesLtba, websitesToSet);
            } else {
                store.dispatch(publisherActionTypes.setSelectedWebsitesYield, websitesToSet);
            }

            // Update localStorage with website IDs
            const { getValidAabSiteIds } = await import("@/helpers/data");
            const websiteIds = getValidAabSiteIds(websitesToSet);

            if (websiteIds.length > 0 && userId) {
                // Preserve user's previously chosen period (LS or publisher-level Vuex)
                // when caller didn't provide one — keeps period across mode switches/remounts.
                const resolvedPeriod = period || getStoredPeriod(userId) || getDefaultPeriod();
                store.dispatch(websiteDatesActionTypes.setWebsiteDatesToLS, {
                    aabSiteIds: websiteIds,
                    period: resolvedPeriod,
                    userId: userId,
                    namespace: namespace,
                });
            }

            return websitesToSet;
        }

        return [];
    };

    /**
     * Read previously stored period for this dashboard from its namespace LS.
     * Returns null when nothing usable is found — caller decides whether to default.
     */
    const getStoredPeriod = (userId) => {
        if (!userId) return null;
        const stored = getItem(namespace);
        if (!stored || stored.userId !== userId || !stored.period) return null;
        const p = stored.period;
        // LS persists object periods as {timeFrom,timeTo}; the rest of the app
        // works with {dateFrom,dateTo}. Convert back here so callers can pass
        // the result directly to setWebsiteDatesToLS without crashing on
        // `period.dateFrom.getTime()`.
        if (typeof p === "string") return p;
        if (p.timeFrom != null && p.timeTo != null) {
            return { dateFrom: new Date(p.timeFrom), dateTo: new Date(p.timeTo) };
        }
        return p;
    };

    return {
        clearDashboardData,
        clearDisplayData,
        clearLocalStorage,
        loadWebsites,
        loadAndSetWebsitesForPublisher,
        setupRouteWatcher,
        setupUnmountCleanup,
        handlePublisherChange,
        handleSinglePublisherSelection,
        getStoredPeriod,
        namespace,
        routeName,
        isLtba,
    };
}
